import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Warehouse, Star } from "lucide-react";

const empty = {
  label: "", company: "Premier Handling Solutions", address1: "", city: "", state: "", zip: "",
  contact_name: "", contact_phone: "", fedex_account_number: "", fedex_billing_account: "",
  active: true, is_default: false, sort_order: 0,
};

export default function ShipOrigins() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/ship-origins").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.zip?.trim()) return toast.error("ZIP is required (used to match the origin to quotes).");
    if (!form.fedex_account_number?.trim()) return toast.error("FedEx Freight account number is required.");
    try {
      if (editing) await api.put(`/ship-origins/${editing.id}`, form);
      else await api.post("/ship-origins", form);
      toast.success("Ship-from location saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.label}"?`)) return;
    try { await api.delete(`/ship-origins/${r.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">Ship-From Locations</h1>
          <p className="mt-1 text-sm text-muted-foreground">Your PHS origins and the FedEx Freight account each ships on</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-origin-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><Plus className="mr-1.5 h-4 w-4" />Add Location</Button>
      </div>

      <div className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4 text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">How this drives FedEx rating:</span> when a quote origin ZIP matches a location, FedEx is rated on that location account (outbound). When the destination ZIP matches, it is rated as an inbound (consignee) shipment. Lanes touching none of your locations fall back to a PHS estimate.
      </div>

      <div className="border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Location</TableHead>
              <TableHead>City / State / ZIP</TableHead>
              <TableHead>FedEx Account</TableHead>
              <TableHead>Billing Acct</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground"><Warehouse className="mx-auto mb-2 h-7 w-7 opacity-40" />No ship-from locations yet.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} className="border-border hover:bg-secondary/40" data-testid={`origin-row-${r.id}`}>
                <TableCell className="text-sm font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    {r.is_default && <Star className="h-3.5 w-3.5 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" />}
                    {r.label}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{[r.city, r.state].filter(Boolean).join(", ")} {r.zip}</TableCell>
                <TableCell><span className="font-semibold text-[hsl(var(--primary))]">{r.fedex_account_number || "—"}</span></TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.fedex_billing_account || "—"}</TableCell>
                <TableCell><span className={`inline-block h-2 w-2 rounded-full ${r.active ? "bg-[hsl(var(--success))]" : "bg-zinc-500"}`} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-origin-${r.id}`} onClick={() => { setEditing(r); setForm({ ...empty, ...r }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--destructive))]" data-testid={`delete-origin-${r.id}`} onClick={() => del(r)}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="origin-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Ship-From Location" : "Add Ship-From Location"}</DialogTitle>
            <DialogDescription>Set the address and the FedEx Freight account this origin ships on. ZIP + FedEx account are required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-caps">Label</Label><Input value={form.label} onChange={(e) => setF("label", e.target.value)} className="mt-1.5 h-10" data-testid="origin-label" placeholder="e.g. Elgin, IL — Main DC" /></div>
            <div><Label className="label-caps">Street Address</Label><Input value={form.address1} onChange={(e) => setF("address1", e.target.value)} className="mt-1.5 h-10" data-testid="origin-address1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="label-caps">City</Label><Input value={form.city} onChange={(e) => setF("city", e.target.value)} className="mt-1.5 h-10" data-testid="origin-city" /></div>
              <div><Label className="label-caps">State</Label><Input value={form.state} onChange={(e) => setF("state", e.target.value)} className="mt-1.5 h-10" maxLength={2} data-testid="origin-state" /></div>
              <div><Label className="label-caps">ZIP</Label><Input value={form.zip} onChange={(e) => setF("zip", e.target.value)} className="mt-1.5 h-10" data-testid="origin-zip" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">FedEx Account #</Label><Input value={form.fedex_account_number} onChange={(e) => setF("fedex_account_number", e.target.value)} className="mt-1.5 h-10" data-testid="origin-fedex-account" placeholder="Shipper account" /></div>
              <div><Label className="label-caps">Billing Account #</Label><Input value={form.fedex_billing_account} onChange={(e) => setF("fedex_billing_account", e.target.value)} className="mt-1.5 h-10" data-testid="origin-fedex-billing" placeholder="Defaults to shipper acct" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">Contact Name</Label><Input value={form.contact_name} onChange={(e) => setF("contact_name", e.target.value)} className="mt-1.5 h-10" data-testid="origin-contact-name" /></div>
              <div><Label className="label-caps">Contact Phone</Label><Input value={form.contact_phone} onChange={(e) => setF("contact_phone", e.target.value)} className="mt-1.5 h-10" data-testid="origin-contact-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center justify-between rounded-md border border-border px-3 py-2"><span className="text-sm">Active</span><Switch checked={form.active} onCheckedChange={(v) => setF("active", v)} data-testid="origin-active" /></label>
              <label className="flex items-center justify-between rounded-md border border-border px-3 py-2"><span className="text-sm">Default</span><Switch checked={form.is_default} onCheckedChange={(v) => setF("is_default", v)} data-testid="origin-default" /></label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-origin-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
